/**
 * Editable + skippable bulk-import preview grid.
 *
 * Rebuilds the old read-only PreviewStep table into a wide, horizontally
 * scrollable grid where every mapped field of a BulkImportRow is editable
 * inline. Edits update an immutable working copy (the originally-parsed rows
 * are kept untouched so changed cells can be highlighted). Skipping a row
 * strikes it through and excludes it from both the dry-run and the import.
 *
 * After any edit / skip toggle, a debounced (~400ms) dry-run re-validates the
 * current non-skipped working set so the Action / Error / Note columns reflect
 * the real backend result. The commit sends only the non-skipped working rows.
 *
 * Row identity is the ORIGINAL parse index (stable), so edits, skip state and
 * dry-run results stay aligned even as the sent subset changes. Dry-run
 * results are matched back by the position within the array we sent (we keep a
 * parallel array of the original indices we sent).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Banner } from '../../components/primitives/Banner'
import {
  bulkImportEmployees,
  type BulkImportResult,
  type BulkImportRow,
  type RowResult,
} from '../../api/hr-bulk-import'
import {
  applyEdit,
  Cell,
  FIELDS,
  type FieldKey,
  type FieldKind,
  type WorkingRow,
} from './grid-fields'

const DEBOUNCE_MS = 400

// ── Props ───────────────────────────────────────────────────────────────────────

interface PreviewGridProps {
  rows: BulkImportRow[]
  onBack: () => void
  onCommitted: (result: BulkImportResult) => void
}

// ── Grid ────────────────────────────────────────────────────────────────────────

export function PreviewGrid({ rows, onBack, onCommitted }: PreviewGridProps): JSX.Element {
  const [working, setWorking] = useState<WorkingRow[]>(() =>
    rows.map((r, i) => ({ id: i, original: r, current: r, skipped: false })),
  )

  // The original indices (ids) of the rows in the LAST dry-run we sent, in send
  // order, so we can map result.rows[i] back to a working-row id.
  const [sentIds, setSentIds] = useState<number[]>([])

  const dryRunMutation = useMutation({
    mutationFn: (payload: { rows: BulkImportRow[]; ids: number[] }) => {
      setSentIds(payload.ids)
      return bulkImportEmployees(payload.rows, true)
    },
  })
  const commitMutation = useMutation({
    mutationFn: (payload: BulkImportRow[]) => bulkImportEmployees(payload, false),
    onSuccess: onCommitted,
  })

  // Keep the latest mutate in a ref so the debounce effect doesn't re-subscribe.
  const runDryRunRef = useRef<(rows: WorkingRow[]) => void>(() => {})
  runDryRunRef.current = (rowsNow: WorkingRow[]): void => {
    const active = rowsNow.filter((r) => !r.skipped)
    dryRunMutation.mutate({ rows: active.map((r) => r.current), ids: active.map((r) => r.id) })
  }

  // Initial dry-run on mount.
  useEffect(() => {
    runDryRunRef.current(working)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced re-run on edits / skip changes (skips the very first render —
  // the mount effect above already fired one).
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    const handle = setTimeout(() => runDryRunRef.current(working), DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [working])

  function handleEdit(id: number, key: FieldKey, kind: FieldKind, raw: string): void {
    setWorking((prev) =>
      prev.map((r) => (r.id === id ? applyEdit(r, key, kind, raw) : r)),
    )
  }

  function handleToggleSkip(id: number): void {
    setWorking((prev) => prev.map((r) => (r.id === id ? { ...r, skipped: !r.skipped } : r)))
  }

  // Map dry-run results back to working-row id via the parallel sentIds array.
  const resultById = useMemo(() => {
    const map = new Map<number, RowResult>()
    const result = dryRunMutation.data
    if (result) {
      for (const r of result.rows) {
        const id = sentIds[r.rowIndex]
        if (id !== undefined) map.set(id, r)
      }
    }
    return map
  }, [dryRunMutation.data, sentIds])

  const activeRows = working.filter((r) => !r.skipped)
  const skippedCount = working.length - activeRows.length
  const preview = dryRunMutation.data
  const validating = dryRunMutation.isPending

  // Counts reflect only the latest dry-run (which is over non-skipped rows).
  const createCount = preview?.wouldCreate ?? 0
  const updateCount = preview?.wouldUpdate ?? 0
  const errorCount = preview?.errorCount ?? 0
  const importCount = createCount + updateCount

  const allErrored = preview ? preview.errorCount === preview.total && preview.total > 0 : false
  const commitDisabled =
    validating || commitMutation.isPending || activeRows.length === 0 || allErrored

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <SummaryStat label="To create" value={createCount} color="var(--success-fg)" />
        <SummaryStat label="To update" value={updateCount} color="var(--steel-blue)" />
        <SummaryStat
          label="Errors"
          value={errorCount}
          color={errorCount > 0 ? 'var(--danger-fg)' : 'var(--text-3)'}
        />
        <SummaryStat label="Skipped" value={skippedCount} color="var(--text-3)" />
        <span
          aria-live="polite"
          style={{
            fontSize: 12,
            color: 'var(--text-3)',
            opacity: validating ? 1 : 0,
            transition: 'opacity 120ms ease',
          }}
        >
          Validating…
        </span>
      </div>

      {errorCount > 0 ? (
        <Banner
          kind="warning"
          title={`${errorCount} row${errorCount === 1 ? '' : 's'} have errors and will be skipped`}
          subtitle="Fix the cell values below to include them, or skip them explicitly."
        />
      ) : null}

      {dryRunMutation.isError ? (
        <Banner
          kind="conflict"
          title="Dry-run failed"
          subtitle={
            dryRunMutation.error instanceof Error
              ? dryRunMutation.error.message
              : 'Server error during validation'
          }
        />
      ) : null}

      {/* Wide editable grid */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
        <table
          data-testid="preview-table"
          style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}
        >
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              <HeaderCell>#</HeaderCell>
              <HeaderCell>Skip</HeaderCell>
              {FIELDS.map((f) => (
                <HeaderCell key={f.key}>{f.label}</HeaderCell>
              ))}
              <HeaderCell>Action</HeaderCell>
              <HeaderCell>Note</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {working.map((row) => {
              const result = resultById.get(row.id)
              const isError = result?.action === 'error'
              return (
                <tr
                  key={row.id}
                  data-testid="preview-row"
                  data-skipped={row.skipped ? 'true' : 'false'}
                  style={{
                    background: row.skipped
                      ? 'var(--surface-2)'
                      : isError
                        ? 'var(--danger-bg)'
                        : undefined,
                    opacity: row.skipped ? 0.5 : 1,
                    textDecoration: row.skipped ? 'line-through' : 'none',
                    transition: 'opacity 120ms ease',
                  }}
                >
                  <td style={{ padding: '4px 8px', color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
                    {row.id + 1}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>
                    <button
                      type="button"
                      data-testid="skip-toggle"
                      aria-pressed={row.skipped}
                      onClick={() => handleToggleSkip(row.id)}
                      style={{
                        padding: '3px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 4,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        border: '1px solid var(--border)',
                        background: row.skipped ? 'var(--steel-blue)' : 'var(--surface)',
                        color: row.skipped ? 'var(--surface)' : 'var(--text-2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.skipped ? 'Skipped' : 'Skip'}
                    </button>
                  </td>
                  {FIELDS.map((f) => (
                    <Cell key={f.key} field={f} row={row} onEdit={handleEdit} />
                  ))}
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    {row.skipped ? (
                      <span style={{ color: 'var(--text-3)', fontSize: 11 }}>Skipped</span>
                    ) : isError ? (
                      <span style={{ color: 'var(--danger-fg)', fontWeight: 600, fontSize: 11 }}>
                        Error: {result?.error}
                      </span>
                    ) : result ? (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          textDecoration: 'none',
                          background:
                            result.action === 'create' ? 'var(--success-bg)' : 'var(--primary-bg)',
                          color:
                            result.action === 'create' ? 'var(--success-fg)' : 'var(--steel-blue)',
                        }}
                      >
                        {result.action === 'create' ? 'Create' : 'Update'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-3)', fontSize: 11 }}>—</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '4px 8px',
                      borderBottom: '1px solid var(--border)',
                      color: 'var(--warning-fg, var(--text-3))',
                      fontSize: 11,
                      minWidth: 120,
                    }}
                  >
                    {result?.note ?? ''}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {commitMutation.isError ? (
        <Banner
          kind="conflict"
          title="Commit failed"
          subtitle={
            commitMutation.error instanceof Error
              ? commitMutation.error.message
              : 'Server error during import'
          }
        />
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onBack}
          disabled={commitMutation.isPending}
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Back
        </button>
        <button
          type="button"
          data-testid="commit-btn"
          disabled={commitDisabled}
          onClick={() =>
            commitMutation.mutate(activeRows.map((r) => r.current))
          }
          style={{
            padding: '8px 16px',
            background: 'var(--steel-blue)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            cursor: commitDisabled ? 'not-allowed' : 'pointer',
            opacity: commitDisabled ? 0.6 : 1,
            transition: 'opacity 120ms ease',
          }}
        >
          {commitMutation.isPending
            ? 'Importing…'
            : `Import ${importCount} employee${importCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}

// ── Small presentational helpers ─────────────────────────────────────────────────

function HeaderCell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <th
      style={{
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
        color: 'var(--text-3)',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        borderBottom: '1px solid var(--border)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  )
}

interface SummaryStatProps {
  label: string
  value: number
  color: string
}

function SummaryStat({ label, value, color }: SummaryStatProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 20, fontWeight: 700, color }}>{value}</span>
      <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </span>
    </div>
  )
}
